// Robust sign-out orchestration. The key invariant: a sign-out must ALWAYS
// leave the user signed out locally, even when their access token is already
// expired or revoked. The old flow did network calls (cancel scheduled pushes,
// OneSignal unbind, global revoke) BEFORE the local sign-out, so a dead session
// caused those calls to 401/hang and the user could never escape. Here every
// remote step is best-effort and time-boxed, and the guaranteed local sign-out
// runs last with a direct-storage-wipe fallback.

const STEP_TIMEOUT_MS = 4000;

// Race a promise against a timeout. Never rejects: resolves to the value on
// success, or undefined on error/timeout. Used to keep best-effort steps from
// blocking the guaranteed local sign-out.
function settleWithin<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout>;
  const timeout = new Promise<undefined>((resolve) => {
    timer = setTimeout(() => resolve(undefined), ms);
  });
  return Promise.race([
    Promise.resolve(promise).catch(() => undefined),
    timeout,
  ]).finally(() => clearTimeout(timer));
}

// Remove Supabase's persisted auth-token entries directly. Last-resort fallback
// for when even a local signOut() call fails — guarantees the stored session is
// gone so the app can't reload back into a broken authed state.
export function clearSupabaseAuthStorage(storage: Storage = localStorage): void {
  try {
    const keys = Object.keys(storage).filter(
      (k) => k.startsWith("sb-") && k.includes("-auth-token")
    );
    keys.forEach((k) => storage.removeItem(k));
  } catch {
    // ignore — storage may be unavailable
  }
}

// Distinguishes a genuine "this token is bad" rejection from a transient
// network failure. getUser()/getSession() return an error in BOTH cases, but
// only an auth rejection (HTTP 401/403) means the session is actually invalid.
// A connectivity error (offline, flaky network, timeout) must NOT trigger a
// sign-out, or users who open the app offline get spuriously logged out.
export function isInvalidSessionError(error: unknown): boolean {
  if (!error || typeof error !== "object") return false;
  const status = (error as { status?: number }).status;
  return status === 401 || status === 403;
}

export interface SignOutResult {
  error?: unknown;
}

export interface SignOutDeps {
  userId?: string | null;
  // Best-effort: cancel scheduled OneSignal pushes (needs a valid token).
  invokeCancelPushes?: () => Promise<SignOutResult | unknown>;
  // Best-effort: unbind the OneSignal external id via the native bridge.
  unbindPushDevice?: () => Promise<unknown>;
  // Best-effort: revoke the session server-side.
  signOutGlobal?: () => Promise<SignOutResult | unknown>;
  // Guaranteed: clear the local session.
  signOutLocal: () => Promise<SignOutResult | unknown>;
  // Last-resort storage wipe if signOutLocal itself fails.
  clearStorage?: () => void;
  timeoutMs?: number;
}

// Returns true if the local session was definitively cleared.
export async function performSignOut(deps: SignOutDeps): Promise<boolean> {
  const timeout = deps.timeoutMs ?? STEP_TIMEOUT_MS;

  // --- best-effort remote cleanups (time-boxed, never throw) ---
  if (deps.userId && deps.invokeCancelPushes) {
    await settleWithin(Promise.resolve(deps.invokeCancelPushes()), timeout);
  }
  if (deps.unbindPushDevice) {
    await settleWithin(Promise.resolve(deps.unbindPushDevice()), timeout);
  }
  if (deps.signOutGlobal) {
    await settleWithin(Promise.resolve(deps.signOutGlobal()), timeout);
  }

  // --- guaranteed local sign-out ---
  let cleared = false;
  try {
    const res = (await settleWithin(
      Promise.resolve(deps.signOutLocal()),
      timeout
    )) as SignOutResult | undefined;
    cleared = !!res && !res.error;
  } catch {
    cleared = false;
  }

  if (!cleared) {
    // Local sign-out errored or timed out — wipe the stored session directly.
    deps.clearStorage?.();
    cleared = true;
  }

  return cleared;
}
